<template>
  <component
    :is="getElementType()"
    :to="to"
    :href="href"
    :target="href ? '_blank' : undefined"
    :rel="href ? 'noopener noreferrer' : undefined"
    :class="[
      'whitespace-nowrap inline-flex items-center justify-center border rounded-full font-semibold transition-colors',
      disabled ? 'cursor-not-allowed' : 'cursor-pointer',
      sizeClass,
      colorClass,
      customClass,
    ]"
    :type="to || href ? undefined : type"
  >
    <slot />
  </component>
</template>

<script setup lang="ts">
  import { computed } from 'vue';

  export type ButtonColor = 'primary' | 'accent' | 'default' | 'muted' | 'transparent';

  const props = withDefaults(
    defineProps<{
      is?: string;
      type?: 'button' | 'submit' | 'reset';
      size?: 'sm' | 'md' | 'lg';
      color?: ButtonColor;
      customClass?: string;
      to?: string;
      href?: string;
      disabled?: boolean;
    }>(),
    {
      type: 'button',
      size: 'md',
      color: 'transparent',
      customClass: '',
      to: undefined,
      href: undefined,
    }
  );

  const getElementType = () => {
    if (props.is) {
      return props.is;
    }

    if (props.to) {
      return 'router-link';
    }

    if (props.href) {
      return 'a'; // More descriptive than 'a'
    }

    return 'button';
  };

  const sizeClass = computed(() => ({
    sm: 'px-4 py-2 text-sm',
    md: 'px-6 py-3',
    lg: 'px-8 py-4',
  }[props.size]));

  const colorClass = computed(() => ({
    primary:
      'bg-primary text-primary-text hover:bg-primary-dark border-primary',
    accent: 'bg-accent text-accent-text hover:opacity-90 border-accent',
    transparent: 'text-headings hover:opacity-90 border-transparent',
    default:
      'bg-surface-light border-surface-grey text-headings hover:opacity-90',
    muted: 'bg-surface-light text-text border-surface-grey hover:opacity-90',
  }[props.color]));
</script>
